import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, TextField,
  MenuItem, Select, FormControl, InputLabel, IconButton, Tooltip,
  LinearProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Inventory as InventoryIcon, Warning, CheckCircle, Cancel,
  PauseCircle, Visibility, Timeline,
} from '@mui/icons-material';

const API = '/api/wmc';

const statusConfig: Record<string, { color: string; label: string; icon: React.ReactElement }> = {
  quarantine: { color: '#F59E0B', label: 'Quarantine', icon: <Warning fontSize="small" /> },
  inspection: { color: '#3B82F6', label: 'Inspection', icon: <Visibility fontSize="small" /> },
  accepted: { color: '#10B981', label: 'Accepted', icon: <CheckCircle fontSize="small" /> },
  rejected: { color: '#EF4444', label: 'Rejected', icon: <Cancel fontSize="small" /> },
  on_hold: { color: '#8B5CF6', label: 'On Hold', icon: <PauseCircle fontSize="small" /> },
  expired: { color: '#6B7280', label: 'Expired', icon: <Warning fontSize="small" /> },
  consumed: { color: '#6B7280', label: 'Consumed', icon: <CheckCircle fontSize="small" /> },
  returned: { color: '#F97316', label: 'Returned', icon: <Cancel fontSize="small" /> },
};

interface Lot {
  id: string;
  lot_number: string;
  material_name: string | null;
  material_code: string | null;
  supplier_name: string | null;
  supplier_lot_number: string | null;
  status: string;
  quantity_received: string;
  quantity_available: string;
  quantity_allocated: string;
  quantity_consumed: string;
  unit: string;
  location: string | null;
  expiry_date: string | null;
  days_in_current_status: number;
  created_at: string;
  is_product_contact: boolean;
  notes: string | null;
}

interface DashboardData {
  total_lots: number;
  quarantine_lots: number;
  accepted_lots: number;
  rejected_lots: number;
  on_hold_lots: number;
  expiring_soon: number;
  active_suppliers: number;
  open_pos: number;
}

interface TraceData {
  lot: Lot;
  forward_trace: Array<{
    batch_id: string;
    batch_number: string;
    batch_status: string;
    quantity_used: number;
    unit: string;
    product_code: string;
    product_name: string;
  }>;
  backward_trace: {
    supplier: { code: string; name: string } | null;
    receiving: { receiving_number: string; received_date: string; received_by: string } | null;
    purchase_order: { po_number: string; ordered_date: string } | null;
  };
}

const WmcInventory: React.FC = () => {
  const [lots, setLots] = useState<Lot[]>([]);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [traceDialog, setTraceDialog] = useState<TraceData | null>(null);

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const [lotsRes, dashRes] = await Promise.all([
        fetch(`${API}/lots/${params}`),
        fetch(`${API}/dashboard/`),
      ]);
      const lotsData = await lotsRes.json();
      const dashData = await dashRes.json();
      setLots(lotsData.results || []);
      setDashboard(dashData);
    } catch (e) {
      console.error('Failed to load inventory', e);
    }
    setLoading(false);
  };

  const loadTrace = async (lotId: string) => {
    try {
      const res = await fetch(`${API}/lots/${lotId}/trace/`);
      const data = await res.json();
      setTraceDialog(data);
    } catch (e) {
      console.error('Failed to load trace', e);
    }
  };

  const filtered = lots.filter(l =>
    !search || 
    l.lot_number.toLowerCase().includes(search.toLowerCase()) ||
    (l.material_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (l.material_code || '').toLowerCase().includes(search.toLowerCase())
  );

  const utilization = (l: Lot) => {
    const recv = parseFloat(l.quantity_received);
    const avail = parseFloat(l.quantity_available);
    return recv > 0 ? ((recv - avail) / recv) * 100 : 0;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InventoryIcon /> Material Inventory
      </Typography>

      {/* Dashboard Cards */}
      {dashboard && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Lots', value: dashboard.total_lots, color: '#3B82F6' },
            { label: 'In Quarantine', value: dashboard.quarantine_lots, color: '#F59E0B' },
            { label: 'Accepted', value: dashboard.accepted_lots, color: '#10B981' },
            { label: 'Rejected', value: dashboard.rejected_lots, color: '#EF4444' },
            { label: 'On Hold', value: dashboard.on_hold_lots, color: '#8B5CF6' },
            { label: 'Expiring Soon', value: dashboard.expiring_soon, color: '#F97316' },
          ].map(card => (
            <Grid item xs={6} sm={4} md={2} key={card.label}>
              <Card sx={{ borderLeft: `4px solid ${card.color}` }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                  <Typography variant="h4" sx={{ color: card.color }}>{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search lots, materials..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ width: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} label="Status">
            <MenuItem value="">All</MenuItem>
            {Object.entries(statusConfig).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Lots Table */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#1F2937' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 'bold', color: '#9CA3AF', borderBottom: '2px solid #374151' } }}>
              <TableCell>Lot #</TableCell>
              <TableCell>Material</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell>Utilization</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Expiry</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(lot => {
              const sc = statusConfig[lot.status] || { color: '#6B7280', label: lot.status, icon: null };
              return (
                <TableRow key={lot.id} hover sx={{ '&:hover': { backgroundColor: 'rgba(59,130,246,0.05)' } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{lot.lot_number}</Typography>
                    {lot.is_product_contact && (
                      <Chip label="Product Contact" size="small" sx={{ fontSize: '0.6rem', height: 18, mt: 0.5, backgroundColor: '#7C3AED', color: '#fff' }} />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{lot.material_name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{lot.material_code}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{lot.supplier_name || '—'}</Typography>
                    {lot.supplier_lot_number && (
                      <Typography variant="caption" color="text.secondary">Lot: {lot.supplier_lot_number}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={sc.icon}
                      label={sc.label}
                      size="small"
                      sx={{ backgroundColor: `${sc.color}20`, color: sc.color, fontWeight: 'bold' }}
                    />
                    <Typography variant="caption" display="block" color="text.secondary">
                      {lot.days_in_current_status}d
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {parseFloat(lot.quantity_available).toFixed(1)} {lot.unit}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      of {parseFloat(lot.quantity_received).toFixed(1)}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>
                    <LinearProgress
                      variant="determinate"
                      value={utilization(lot)}
                      sx={{
                        height: 8, borderRadius: 4,
                        backgroundColor: '#374151',
                        '& .MuiLinearProgress-bar': { backgroundColor: utilization(lot) > 80 ? '#EF4444' : '#3B82F6' }
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">{utilization(lot).toFixed(0)}%</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{lot.location || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {lot.expiry_date ? (
                      <Typography variant="body2" sx={{
                        color: new Date(lot.expiry_date) < new Date(Date.now() + 30 * 86400000) ? '#F59E0B' : '#D1D5DB'
                      }}>
                        {lot.expiry_date}
                      </Typography>
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Traceability">
                      <IconButton size="small" onClick={() => loadTrace(lot.id)}>
                        <Timeline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No lots found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Traceability Dialog */}
      <Dialog open={!!traceDialog} onClose={() => setTraceDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#1F2937' }}>
          <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
          Lot Traceability — {traceDialog?.lot?.lot_number}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1F2937' }}>
          {traceDialog && (
            <Box>
              {/* Backward Trace */}
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>⬅ Backward Trace (Supply Chain)</Typography>
              <Box sx={{ pl: 2, borderLeft: '3px solid #3B82F6' }}>
                {traceDialog.backward_trace.supplier && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Supplier</Typography>
                    <Typography>{traceDialog.backward_trace.supplier.name} ({traceDialog.backward_trace.supplier.code})</Typography>
                  </Box>
                )}
                {traceDialog.backward_trace.purchase_order && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Purchase Order</Typography>
                    <Typography>{traceDialog.backward_trace.purchase_order.po_number} — Ordered: {traceDialog.backward_trace.purchase_order.ordered_date}</Typography>
                  </Box>
                )}
                {traceDialog.backward_trace.receiving && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Receiving</Typography>
                    <Typography>{traceDialog.backward_trace.receiving.receiving_number} — By: {traceDialog.backward_trace.receiving.received_by}</Typography>
                  </Box>
                )}
              </Box>

              {/* Forward Trace */}
              <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>➡ Forward Trace (Batches)</Typography>
              {traceDialog.forward_trace.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Batch</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Qty Used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {traceDialog.forward_trace.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell>{t.batch_number}</TableCell>
                        <TableCell>{t.product_name} ({t.product_code})</TableCell>
                        <TableCell>
                          <Chip label={t.batch_status} size="small" />
                        </TableCell>
                        <TableCell align="right">{t.quantity_used} {t.unit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary" sx={{ pl: 2 }}>No batches linked to this lot yet</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1F2937' }}>
          <Button onClick={() => setTraceDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WmcInventory;
